import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  useCreatePaymentMutation,
  useEditPaymentMutation,
} from "../slices/paymentSlice";
import { getCurrentDate } from "../utils/getCurrentDate";
import { formatAmount } from "../utils/commonUtils";

const paymentModes = [
  "Cash",
  "UPI",
  "Debit Card",
  "Credit Card",
  "Net Banking",
  "Gpay",
  "PhonePe",
  "PayTM",
  "Amazon Pay",
  "Wallet",
  "Check",
  "Demand Draft",
  "Others",
];

// Create / edit payment modal.
// - Pass `order` to create a payment against that order (orderId prefilled).
// - Pass `payment` to edit an existing payment (fields prefilled and
//   paidAmount locked, since the api does not allow updating it).
const PaymentModal = ({ show, onHide, order, payment, onSuccess }) => {
  const { userInfo } = useSelector((state) => state.auth);
  const isEditMode = Boolean(payment);

  const [invoiceNumber, setInvoiceNumber] = useState(
    order?.invoiceNumber || payment?.invoiceNumber || ""
  );
  const [paidAmount, setPaidAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(getCurrentDate());
  const [paymentMode, setPaymentMode] = useState("Payment Mode");
  const [paymentReference, setPaymentReference] = useState("");

  const [createPayment, { isLoading: creating }] = useCreatePaymentMutation();
  const [editPayment, { isLoading: updating }] = useEditPaymentMutation();

  useEffect(() => {
    if (!show) return;
    setInvoiceNumber(
      String(order?.invoiceNumber ?? payment?.invoiceNumber ?? "")
    );
    setPaidAmount(
      payment?.paidAmount !== undefined ? String(payment.paidAmount) : ""
    );
    setPaymentDate(
      (payment?.paymentDate || new Date().toISOString()).split("T")[0]
    );
    setPaymentMode(payment?.paymentMode || "Payment Mode");
    setPaymentReference(payment?.paymentReference || "");
  }, [show, payment, order]);

  const verifyInputFields = () => {
    if (!String(invoiceNumber).trim()) {
      toast.error("Invoice number is required");
      return false;
    }
    if (!isEditMode && (!paidAmount || Number(paidAmount) < 1)) {
      toast.error("Please provide a valid paid amount");
      return false;
    }
    if (!paymentDate) {
      toast.error("Please select the payment date");
      return false;
    }
    if (paymentMode === "Payment Mode") {
      toast.error("Please select a payment mode");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!verifyInputFields()) return;

    try {
      if (isEditMode) {
        // paidAmount is intentionally omitted - the api rejects updates to it
        await editPayment({
          token: userInfo?.token,
          paymentId: payment._id,
          payload: {
            paymentDate,
            paymentMode,
            paymentReference: paymentReference.trim() || undefined,
          },
        }).unwrap();
        toast.success("Payment updated successfully");
      } else {
        await createPayment({
          token: userInfo?.token,
          payload: {
            order_id: order?._id,
            paidAmount: Number(paidAmount),
            paymentDate,
            paymentMode,
            paymentReference: paymentReference.trim() || undefined,
          },
        }).unwrap();
        toast.success("Payment created successfully");
      }
      onHide();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.log(err);
      toast.error(
        err?.data?.message ||
          err?.data?.error ||
          `Failed to ${isEditMode ? "update" : "create"} payment`
      );
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="md"
      fullscreen="sm-down"
      scrollable
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.05rem" }}>
          <strong>{isEditMode ? "Edit Payment" : "Create Payment"}</strong>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group className="my-2" controlId="paymentOrderId">
            <Form.Label style={{ color: "black" }}>
              Invoice<span style={{ color: "red" }}> *</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter invoice number"
              value={invoiceNumber}
              disabled={true}
              // onChange={(e) => setInvoiceNumber(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="my-2" controlId="paymentPaidAmount">
            <Form.Label style={{ color: "black" }}>
              Paid Amount<span style={{ color: "red" }}> *</span>
            </Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter paid amount"
              value={paidAmount}
              disabled={isEditMode}
              onChange={(e) => setPaidAmount(e.target.value)}
            />
            {isEditMode ? (
              <small className="text-muted">
                Paid amount cannot be updated
              </small>
            ) : (
              order?.dueAmount > 0 && (
                <small style={{ color: "red" }}>
                  Due amount : Rs.{formatAmount(order.dueAmount)}
                </small>
              )
            )}
          </Form.Group>

          <Row>
            <Col xs={12} sm={6}>
              <Form.Group className="my-2" controlId="paymentDate">
                <Form.Label style={{ color: "black" }}>
                  Payment Date<span style={{ color: "red" }}> *</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Group className="my-2" controlId="paymentMode">
                <Form.Label style={{ color: "black" }}>
                  Payment Mode<span style={{ color: "red" }}> *</span>
                </Form.Label>
                <Form.Select
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  style={{
                    color:
                      paymentMode === "Payment Mode" ? "#6c757d" : "inherit",
                  }}
                >
                  <option value="Payment Mode" disabled>
                    Payment Mode
                  </option>
                  {paymentModes.map((mode) => (
                    <option key={mode} value={mode}>
                      {mode}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="my-2" controlId="paymentReference">
            <Form.Label style={{ color: "black" }}>
              Payment Reference <small className="text-muted">(optional)</small>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Transaction id / UTR / reference note"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div
          className="d-flex gap-2"
          style={{ width: "100%", maxWidth: "420px", margin: "0 auto" }}
        >
          <Button
            className={`${userInfo?.themeColor}ThemeHeaderTop`}
            style={{ flex: 1, padding: "0.6rem" }}
            onClick={handleSubmit}
            disabled={creating || updating}
          >
            {creating || updating
              ? "Submitting..."
              : isEditMode
              ? "Update"
              : "Submit"}
          </Button>{" "}
          <Button
            variant="secondary"
            style={{ flex: 1, padding: "0.6rem" }}
            onClick={onHide}
          >
            Cancel
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default PaymentModal;
